/**
 * Client-side image compression and LQIP placeholder generation.
 *
 * - Resizes images that exceed 2400×2400 while preserving aspect ratio
 * - Compresses to WebP (with JPEG fallback) at 0.85 quality
 * - Preserves PNGs with transparency as PNG (resized only)
 * - Skips GIFs entirely (may be animated)
 * - Generates tiny ~32px-wide LQIP placeholders as base64 data URLs
 */

const MAX_DIMENSION = 2400
const JPEG_QUALITY = 1
const PLACEHOLDER_WIDTH = 32
const PLACEHOLDER_QUALITY = 0.3

/**
 * Load a File into an HTMLImageElement.
 */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    const url = URL.createObjectURL(file)
    img.src = url
    // Revoke after load to avoid memory leaks
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
  })
}

/**
 * Check if a PNG has any transparent pixels by sampling the alpha channel.
 */
function hasTransparency(img: HTMLImageElement): boolean {
  const canvas = document.createElement('canvas')
  // Sample at reduced size for performance (max 256px)
  const scale = Math.min(1, 256 / Math.max(img.naturalWidth, img.naturalHeight))
  canvas.width = Math.round(img.naturalWidth * scale)
  canvas.height = Math.round(img.naturalHeight * scale)
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) return true
  }
  return false
}

/**
 * Check if browser supports WebP encoding via canvas.
 */
let _webpSupported: boolean | null = null
function supportsWebP(): boolean {
  if (_webpSupported !== null) return _webpSupported
  const canvas = document.createElement('canvas')
  canvas.width = 1
  canvas.height = 1
  _webpSupported = canvas.toDataURL('image/webp').startsWith('data:image/webp')
  return _webpSupported
}

/**
 * Draw an image onto a canvas at the target dimensions, returning the canvas.
 */
function drawToCanvas(
  img: HTMLImageElement,
  width: number,
  height: number,
): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(img, 0, 0, width, height)
  return canvas
}

/**
 * Convert a canvas to a File with the specified format and quality.
 */
function canvasToFile(
  canvas: HTMLCanvasElement,
  fileName: string,
  mimeType: string,
  quality: number,
): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas toBlob returned null'))
          return
        }
        // Adjust extension to match output format
        const ext = mimeType === 'image/webp' ? '.webp' : mimeType === 'image/png' ? '.png' : '.jpg'
        const baseName = fileName.replace(/\.[^.]+$/, '')
        resolve(new File([blob], `${baseName}${ext}`, { type: mimeType }))
      },
      mimeType,
      quality,
    )
  })
}

/**
 * Calculate target dimensions respecting MAX_DIMENSION while preserving
 * aspect ratio. Returns original dimensions if already within bounds.
 */
function targetDimensions(
  w: number,
  h: number,
): { width: number; height: number; resized: boolean } {
  if (w <= MAX_DIMENSION && h <= MAX_DIMENSION) {
    return { width: w, height: h, resized: false }
  }
  const ratio = Math.min(MAX_DIMENSION / w, MAX_DIMENSION / h)
  return {
    width: Math.round(w * ratio),
    height: Math.round(h * ratio),
    resized: true,
  }
}

/**
 * Compress an image file before upload.
 *
 * - GIFs pass through untouched (may be animated)
 * - PNGs with transparency stay PNG but are resized if needed
 * - Everything else → WebP (or JPEG fallback) at 0.85 quality
 * - Images already within 2400×2400 are still recompressed for size savings
 */
export async function compressImage(file: File): Promise<File> {
  // Skip GIFs — may be animated
  if (file.type === 'image/gif') {
    return file
  }

  const img = await loadImage(file)
  const { width, height } = targetDimensions(
    img.naturalWidth,
    img.naturalHeight,
  )

  // PNG with transparency: keep as PNG, only resize
  if (file.type === 'image/png' && hasTransparency(img)) {
    if (!width || !height) return file
    const canvas = drawToCanvas(img, width, height)
    const result = await canvasToFile(canvas, file.name, 'image/png', 1)
    console.debug(
      `[image-compress] PNG with transparency: ${(file.size / 1024).toFixed(0)}KB → ${(result.size / 1024).toFixed(0)}KB (${width}×${height})`,
    )
    return result
  }

  // For everything else: WebP if supported, else JPEG
  const outputType = supportsWebP() ? 'image/webp' : 'image/jpeg'
  const canvas = drawToCanvas(img, width, height)
  const result = await canvasToFile(canvas, file.name, outputType, JPEG_QUALITY)
  console.debug(
    `[image-compress] ${file.type} → ${outputType}: ${(file.size / 1024).toFixed(0)}KB → ${(result.size / 1024).toFixed(0)}KB (${width}×${height})`,
  )
  return result
}

/**
 * Generate a tiny LQIP (Low Quality Image Placeholder) as a base64 data URL.
 *
 * Produces a ~32px-wide thumbnail at JPEG quality 0.3 — typically 200–500 bytes.
 * This is small enough to store inline in the note's Tiptap JSON content.
 */
export async function generatePlaceholder(file: File): Promise<string> {
  const img = await loadImage(file)
  const ratio = img.naturalHeight / img.naturalWidth
  const w = PLACEHOLDER_WIDTH
  const h = Math.round(w * ratio)

  const canvas = drawToCanvas(img, w, h)
  return canvas.toDataURL('image/jpeg', PLACEHOLDER_QUALITY)
}
